import React from 'react';
import {
  Drawer,
  Space,
  Typography,
  Button,
  Tag,
  Card,
  Row,
  Col,
  message,
  Switch,
} from 'antd';
import {
  PhoneOutlined,
  MailOutlined,
  MessageOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CalendarOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { AdministratorLead } from '../../types/administratorLeads';
import { formatDateLocale } from '../../utils/date';
import { useAuth } from '../../contexts/AuthContext';
import { generateWhatsAppMessage, createWhatsAppUrl } from '../../utils/whatsappMessage';
import { getFirstName, getFullName } from '../../utils/text';

const { Title, Text, Paragraph } = Typography;

interface AdministratorLeadDetailDrawerProps {
  lead: AdministratorLead | null;
  open: boolean;
  onClose: () => void;
  onUpdateStatus?: (leadId: string, status: string) => void;
  onToggleWhatsApp?: (leadId: string, unavailable: boolean) => void;
  onEdit?: (lead: AdministratorLead) => void;
}

const AdministratorLeadDetailDrawer: React.FC<AdministratorLeadDetailDrawerProps> = ({
  lead,
  open,
  onClose,
  onUpdateStatus,
  onToggleWhatsApp,
  onEdit,
}) => {
  const { t, i18n } = useTranslation();
  const { firestoreUser } = useAuth();
  const lang = i18n.language || 'en-US';

  const handleCallClick = (phone: string) => {
    if (!phone) return;

    // Format phone number for call (same logic as discover tab)
    let cleanPhone = phone.replace(/\D/g, ''); // Remove non-digits
    
    // Add country code if missing
    if (cleanPhone.length === 10) {
      cleanPhone = '1' + cleanPhone; // Add US country code
    } else if (cleanPhone.length === 11 && cleanPhone.startsWith('1')) {
      // Already has US country code
    } else if (cleanPhone.length === 9 && cleanPhone.startsWith('9')) {
      cleanPhone = '56' + cleanPhone; // Add Chile country code
    } else if (cleanPhone.length === 8 && cleanPhone.startsWith('2')) {
      cleanPhone = '56' + cleanPhone; // Add Chile country code for mobile
    }

    const telUrl = `tel:+${cleanPhone}`;
    window.open(telUrl);
  };

  const handleWhatsAppClick = (phone: string, leadId: string, leadName?: string) => {
    if (!phone) return;

    // Get the first name from the lead name and convert to proper case
    const firstName = getFirstName(leadName || 'Administrator');
    
    // Get the user's name from auth context and convert to proper case
    const userName = getFirstName(firestoreUser?.displayName || 'Sales Manager');
    
    // Debug logging
    console.log('WhatsApp Debug:', {
      originalLeadName: leadName,
      convertedFirstName: firstName,
      originalUserDisplayName: firestoreUser?.displayName,
      convertedUserName: userName
    });
    
    // Generate the WhatsApp message using the utility function
    const whatsappMessage = generateWhatsAppMessage(t, firstName, userName);
    
    // Create WhatsApp URL with message
    const whatsappUrl = createWhatsAppUrl(phone, whatsappMessage);
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    // Update status to Contacted when WhatsApp message is sent
    if (onUpdateStatus) {
      onUpdateStatus(leadId, 'Contacted');
      message.success(t('sales:administratorLeads.messages.whatsappSent', 'WhatsApp message opened'));
    }
  };

  const handleWhatsAppToggle = (unavailable: boolean) => {
    if (lead && onToggleWhatsApp) {
      onToggleWhatsApp(lead.id, unavailable);
      message.success(
        unavailable 
          ? t('sales:administratorLeads.messages.whatsappTagged', 'Marked as WhatsApp unavailable')
          : t('sales:administratorLeads.messages.whatsappAvailable', 'Marked as WhatsApp available')
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New':
        return 'blue';
      case 'Contacted':
        return 'green';
      case 'Lost':
        return 'red';
      case 'Disqualified':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'MINVU Condominios':
        return 'purple';
      case 'Website':
        return 'blue';
      case 'Referral':
        return 'green';
      case 'Social Media':
        return 'cyan';
      case 'Google Places':
        return 'orange';
      default:
        return 'default';
    }
  };

  if (!lead) return null;

  const primaryContact = lead.contacts?.find(c => c.type === 'Primary');

  return (
    <Drawer
      title={
        <Space>
          <UserOutlined />
          {getFullName(lead.name)}
        </Space>
      }
      placement="right"
      width={500}
      onClose={onClose}
      open={open}
      extra={
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => onEdit?.(lead)}
        >
          {t('common:edit')}
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Status and Source */}
        <Card size="small">
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Space direction="vertical" size="small">
                <Text strong>{t('sales:administratorLeads.table.status')}</Text>
                <Tag color={getStatusColor(lead.status)}>
                  {t(`sales:administratorLeads.status.${lead.status.toLowerCase()}`, lead.status)}
                </Tag>
              </Space>
            </Col>
            <Col span={12}>
              <Space direction="vertical" size="small">
                <Text strong>{t('sales:administratorLeads.table.source')}</Text>
                <Tag color={getSourceColor(lead.source)}>
                  {lead.source}
                </Tag>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Contact Information */}
        <div>
          <Title level={5}>{t('sales:discovery.contactInfo')}</Title>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {primaryContact?.phone && (
              <Card size="small">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space>
                    <PhoneOutlined />
                    <Text strong>{primaryContact.phone}</Text>
                    {lead.whatsapp_unavailable && (
                      <Tag color="red">
                        {t('sales:administratorLeads.tags.noWhatsApp', 'No WhatsApp')}
                      </Tag>
                    )}
                  </Space>
                  <Space wrap>
                    <Button
                      type="primary"
                      size="small"
                      icon={<PhoneOutlined />}
                      onClick={() => handleCallClick(primaryContact.phone)}
                    >
                      {t('common:call')}
                    </Button>
                    <Button
                      type="default"
                      size="small"
                      icon={<MessageOutlined />}
                      disabled={lead.whatsapp_unavailable}
                      onClick={() => handleWhatsAppClick(primaryContact.phone, lead.id, lead.name)}
                    >
                      {t('common:whatsapp')}
                    </Button>
                  </Space>
                </Space>
              </Card>
            )}

            {primaryContact?.email && (
              <Card size="small">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space>
                    <MailOutlined />
                    <Text strong>{primaryContact.email}</Text>
                    {lead.status === 'Lost' && (
                      <Tag color="red">
                        {t('sales:administratorLeads.tags.emailUnsubscribed', 'Email Unsubscribed')}
                      </Tag>
                    )}
                  </Space>
                  <Button
                    type="link"
                    size="small"
                    disabled={lead.status === 'Lost'}
                    onClick={() => {
                      const firstName = getFirstName(lead.name || 'Administrador');
                      const subject = `${firstName}, ¿eres administrador de condominios? Este correo es para ti.`;
                      
                      const body = `Hola ${firstName},

Encontré tu contacto a través del registro de administradores de condominios del Minvu y quería ponerme en contacto contigo.

Mi nombre es Dafne Palacios, y gestiono las relaciones con administradores de apartamentos para Chile en My Apartment App.

Ofrecemos un software de administración de condominios que ayuda a los administradores a calcular y cobrar gastos comunes, gestionar residentes, empleados y proveedores, y mantener el control de contabilidad y cumplimiento normativo, todo en una sola plataforma. Más información en https://myapartment.app/cl.html 

Actualmente estamos ejecutando un programa de referidos: si recomiendas My Apartment App para tu condominio o a otra comunidad cercana, Obtén hasta $100.000CLP en comisión por referencia.

Es nuestra forma de agradecerte por ayudarnos a crecer mientras apoyamos a administradores como tú con mejores herramientas.

Si te interesa, puedes agendar una llamada rápida de 15 minutos conmigo aquí: https://calendly.com/myapartment-support/15min

Saludos cordiales,

---
Si no deseas recibir más correos electrónicos, puedes darte de baja aquí: https://internal.myapartment.app/unsubscribe?token=${lead.unsubscribe_token || lead.id}&email=${encodeURIComponent(primaryContact.email)}
(Al darte de baja, tu registro será marcado como "Perdido" en nuestro sistema)`;


                      // Detect mobile device and use appropriate Gmail URL
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      
                      let gmailUrl;
                      if (isMobile) {
                        // Use Gmail app URL scheme for mobile devices
                        gmailUrl = `googlegmail://co?to=${encodeURIComponent(primaryContact.email)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                      } else {
                        // Use web Gmail for desktop
                        gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(primaryContact.email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                      }
                      
                      // Try to open Gmail app, fallback to web if it fails
                      const gmailWindow = window.open(gmailUrl, '_blank');
                      
                      // If mobile and Gmail app didn't open, fallback to web Gmail
                      if (isMobile && gmailWindow) {
                        setTimeout(() => {
                          if (gmailWindow.closed || gmailWindow.location.href === 'about:blank') {
                            const webGmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(primaryContact.email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                            window.open(webGmailUrl, '_blank');
                          }
                        }, 1000);
                      }
                      
                      // Update status to Contacted when email is opened
                      if (onUpdateStatus) {
                        onUpdateStatus(lead.id, 'Contacted');
                        message.success(t('sales:administratorLeads.messages.emailSent', 'Email opened'));
                      }
                    }}
                  >
                    {t('common:email', 'Send Email')}
                  </Button>
                </Space>
              </Card>
            )}
          </Space>
        </div>

        {/* WhatsApp Availability Toggle */}
        <div>
          <Title level={5}>{t('sales:administratorLeads.whatsappSettings', 'WhatsApp Settings')}</Title>
          <Card size="small">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space>
                <MessageOutlined />
                <Text strong>{t('sales:administratorLeads.whatsappAvailable', 'WhatsApp Available')}</Text>
              </Space>
              <Space>
                <Switch
                  checked={!lead.whatsapp_unavailable}
                  onChange={(checked) => handleWhatsAppToggle(!checked)}
                  checkedChildren={t('common:yes')}
                  unCheckedChildren={t('common:no')}
                />
                <Text type="secondary">
                  {lead.whatsapp_unavailable 
                    ? t('sales:administratorLeads.whatsappUnavailableDesc', 'This administrator is not available on WhatsApp')
                    : t('sales:administratorLeads.whatsappAvailableDesc', 'This administrator is available on WhatsApp')
                  }
                </Text>
              </Space>
            </Space>
          </Card>
        </div>

        {/* Address Information */}
        <div>
          <Title level={5}>{t('sales:administratorLeads.addressInfo', 'Address Information')}</Title>
          <Card size="small">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space>
                <EnvironmentOutlined />
                <Text strong>{t('sales:administratorLeads.form.address')}</Text>
              </Space>
              <Text>{lead.address}</Text>
              <Text>{lead.city}, {lead.country}</Text>
              {lead.pincode && <Text>Pin: {lead.pincode}</Text>}
            </Space>
          </Card>
        </div>

        {/* Description */}
        {lead.description && (
          <div>
            <Title level={5}>{t('sales:administratorLeads.form.description')}</Title>
            <Card size="small">
              <Paragraph>{lead.description}</Paragraph>
            </Card>
          </div>
        )}

        {/* Notes */}
        {lead.notes && (
          <div>
            <Title level={5}>{t('sales:administratorLeads.form.notes')}</Title>
            <Card size="small">
              <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{lead.notes}</Paragraph>
            </Card>
          </div>
        )}

        {/* Timestamps */}
        <div>
          <Title level={5}>{t('common:timestamps', 'Timestamps')}</Title>
          <Card size="small">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space>
                <CalendarOutlined />
                <Text strong>{t('common:createdAt')}</Text>
                <Text>{formatDateLocale(lead.created_at, lang)}</Text>
              </Space>
              <Space>
                <CalendarOutlined />
                <Text strong>{t('common:updatedAt')}</Text>
                <Text>{formatDateLocale(lead.updated_at, lang)}</Text>
              </Space>
            </Space>
          </Card>
        </div>
      </Space>
    </Drawer>
  );
};

export default AdministratorLeadDetailDrawer;
